import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Modal, Form, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  User, 
  Users, 
  Settings, 
  Plus, 
  Clock, 
  ArrowLeft, 
  ArrowUpRight
} from 'lucide-react';
import { useSpecialty } from '../contexts/SpecialtyContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import cuoreAnatomico from '../assets/img/cuore-anatomico.png';

interface DashboardAction {
  label: string;
  path: string;
  icon: React.ReactElement;
  clearDraft?: boolean;
  isModal?: boolean;
}

interface DashboardStats {
  fatture_mese_corrente: number;
  referti_mese_corrente: number;
  numero_clienti_totali: number;
  referti_in_bozza: number;
  mese_corrente: number;
  anno_corrente: number;
}

// Numero che sale fino al valore finale
const Counter: React.FC<{ value: number; loading: boolean }> = ({ value, loading }) => {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (loading) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / 900, 1);
      setShown(Math.round(value * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, loading]);

  return <span className="mon-num">{loading ? '–' : shown}</span>;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { selectedSpecialty, getSpecialtyInfo, hasReportsEnabled, hasFatturazioneEnabled, clearSpecialty } = useSpecialty();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    indirizzo: '',
    citta: '',
    email: '',
    codice_fiscale: '',
  });

  // Se non c'è una specialistica selezionata, reindirizza alla selezione
  React.useEffect(() => {
    if (!selectedSpecialty) {
      navigate('/specialty-selection');
    }
  }, [selectedSpecialty, navigate]);

  // Carica le statistiche quando il componente si monta
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const statsData = await apiService.getDashboardStats();
        setStats(statsData);
      } catch (error) {
        console.error('Errore nel caricamento delle statistiche:', error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedSpecialty) {
      loadStats();
    }
  }, [selectedSpecialty]); // Aggiunta dipendenza selectedSpecialty

  const specialtyInfo = getSpecialtyInfo(selectedSpecialty);

  const handleChangeSpecialty = () => {
    clearSpecialty();
    navigate('/specialty-selection');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createCliente(formData);
      toast.success('Cliente creato con successo');
      setShowModal(false);
      resetForm();
      // Ricarica le statistiche per aggiornare il numero clienti
      const statsData = await apiService.getDashboardStats();
      setStats(statsData);
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cognome: '',
      indirizzo: '',
      citta: '',
      email: '',
      codice_fiscale: '',
    });
  };

  // Items sempre presenti
  const basicItems = [
    {
      title: 'Fatture',
      description: 'Gestisci le fatture e genera PDF',
      icon: <FileText size={32} />,
      color: 'primary',
      actions: [
        { label: 'Nuova Fattura', path: '/fatture/nuova', icon: <Plus /> },
        { label: 'Visualizza Fatture', path: `/fatture/${selectedSpecialty}`, icon: <FileText /> },
      ] as DashboardAction[]
    },
    {
      title: 'Clienti',
      description: 'Gestisci l\'anagrafica clienti',
      icon: <Users size={32} />,
      color: 'info',
      actions: [
        { label: 'Nuovo Cliente', path: '', icon: <Plus />, isModal: true },
        { label: 'Visualizza Clienti', path: '/clienti', icon: <Users /> },
      ] as DashboardAction[]
    },
    {
      title: 'Impostazioni',
      description: 'Configura il tuo profilo e logo',
      icon: <Settings size={32} />,
      color: 'secondary',
      actions: [
        { label: 'Profilo', path: '/settings', icon: <Settings /> },
        { label: 'Logo', path: '/settings/logo', icon: <Settings /> },
      ] as DashboardAction[]
    },
  ];

  // Item referti condizionale
  const reportsItem = hasReportsEnabled() ? {
    title: 'Referti',
    description: `Gestisci i referti di ${specialtyInfo?.name}`,
    icon: <User size={32} />,
    color: 'success',
    actions: [
      { label: 'Nuovo Referto', path: `/referti/${specialtyInfo?.name.toLowerCase()}/nuovo`, icon: <Plus />, clearDraft: true },
      { label: 'Visualizza Referti', path: `/referti/${selectedSpecialty}`, icon: <User /> },
    ] as DashboardAction[]
  } : null;

  // Combina gli items
  const dashboardItems = reportsItem 
    ? [basicItems[0], reportsItem, ...basicItems.slice(1)]
    : basicItems;

  const runAction = (action: DashboardAction) => {
    if (action.isModal) {
      setShowModal(true);
    } else {
      navigate(action.path, action.clearDraft ? { state: { clearDraft: true } } : undefined);
    }
  };

  const hour = new Date().getHours();
  const saluto = hour < 13 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const dataOggi = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  const isCardiologia = specialtyInfo?.name.toLowerCase() === 'cardiologia';
  const refertiItem = dashboardItems.find((item) => item.title === 'Referti');
  const bozze = stats?.referti_in_bozza || 0;

  // Due battiti consecutivi: la traccia si ripete in loop
  const ecgPath = 'M0 60 H60 L72 56 L84 60 H120 L130 68 L142 6 L156 108 L166 60 H200 L216 48 L238 66 L250 60 H300 H360 L372 56 L384 60 H420 L430 68 L442 6 L456 108 L466 60 H500 L516 48 L538 66 L550 60 H600';

  return (
    <div className="page-container">
      <Container fluid className="dash">
        <section className="mon">
          <div className="mon-top">
            <span className="mon-chip">
              <span className="mon-live" />
              {specialtyInfo ? `${specialtyInfo.icon} ${specialtyInfo.name}` : 'CardioRef'}
            </span>
            <span className="mon-date">{dataOggi}</span>
            <button type="button" className="mon-switch" onClick={handleChangeSpecialty}>
              <ArrowLeft size={14} /> Cambia specialistica
            </button>
          </div>

          <div className="mon-main">
            <div className="mon-copy">
              <p className="mon-hello">
                {saluto}{profile?.first_name ? `, ${profile.first_name}` : ''}.
              </p>
              <div className="mon-metric">
                <Counter value={stats?.referti_mese_corrente || 0} loading={loading} />
                <span className="mon-metric-label">referti<br />questo mese</span>
              </div>
              <div className="mon-cta">
                {refertiItem && (
                  <button type="button" className="mon-btn mon-btn-main" onClick={() => runAction(refertiItem.actions[0])}>
                    <Plus size={18} /> Nuovo referto
                  </button>
                )}
                {bozze > 0 && (
                  <button
                    type="button"
                    className="mon-btn mon-btn-ghost"
                    onClick={() => navigate(`/referti/${selectedSpecialty}/bozze`)}
                  >
                    <Clock size={16} /> {bozze} {bozze === 1 ? 'bozza in sospeso' : 'bozze in sospeso'}
                  </button>
                )}
              </div>
            </div>

            {isCardiologia && (
              <div className="mon-heart" aria-hidden="true">
                <span className="mon-ring" />
                <span className="mon-ring mon-ring-2" />
                <img src={cuoreAnatomico} alt="" />
              </div>
            )}
          </div>

          <div className="mon-ecg" aria-hidden="true">
            <div className="mon-ecg-track">
              {[0, 1].map((n) => (
                <svg key={n} viewBox="0 0 600 120" preserveAspectRatio="none">
                  <path d={ecgPath} />
                </svg>
              ))}
            </div>
          </div>

          <dl className="mon-readouts">
            <div>
              <dt>Fatture</dt>
              <dd><Counter value={stats?.fatture_mese_corrente || 0} loading={loading} /></dd>
              <small>questo mese</small>
            </div>
            <div>
              <dt>Clienti</dt>
              <dd><Counter value={stats?.numero_clienti_totali || 0} loading={loading} /></dd>
              <small>in anagrafica</small>
            </div>
            <div>
              <dt>Bozze</dt>
              <dd><Counter value={bozze} loading={loading} /></dd>
              <small>da completare</small>
            </div>
          </dl>
        </section>

        <h3 className="dash-section-title">Aree di lavoro</h3>
        <div className="idx">
          {dashboardItems.map((item, index) => (
            <article key={item.title} className="idx-row" style={{ animationDelay: `${index * 0.08}s` }}>
              <span className="idx-num">{String(index + 1).padStart(2, '0')}</span>
              <span className="idx-icon">{item.icon}</span>
              <div className="idx-text">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
              <div className="idx-actions">
                {item.actions.map((action, actionIndex) => (
                  <button
                    key={actionIndex}
                    type="button"
                    className={`idx-pill ${actionIndex === 0 ? 'idx-pill-main' : ''}`}
                    onClick={() => runAction(action)}
                  >
                    {action.label}
                    <ArrowUpRight size={15} />
                  </button>
                ))}
              </div>
            </article>
          ))}
        </div>

        {/* Modal per creazione nuovo cliente */}
        <Modal show={showModal} onHide={() => setShowModal(false)} className="modern-modal">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <User size={20} />
              Nuovo Cliente
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} className="modern-form">
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cognome *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.cognome}
                      onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Indirizzo</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.indirizzo}
                      onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Città</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.citta}
                      onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Codice Fiscale</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.codice_fiscale}
                      onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                type="button" 
                onClick={() => setShowModal(false)}
                className="btn-modern"
              >
                Annulla
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                className="btn-modern d-flex align-items-center gap-2"
              >
                <User size={16} />
                Crea Cliente
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default Dashboard; 